import type { AiCandidate, AiCandidateAdapter, AiGenerationRequest } from "@tattoo-stencil/domain";
import type { StencilGenerationSettings } from "../../../../../api/src/stencilSettings";

export type RemoteAsset = Readonly<{
  id: string;
  contentHash: string;
  mediaType: "image/png" | "image/jpeg";
  byteLength: number;
  widthPx: number;
  heightPx: number;
  expiresAt: string;
}>;

export type RemoteJobStatus = "queued" | "running" | "cancel-requested" | "succeeded" | "failed" | "cancelled";
export type RemoteRegionSelection = Readonly<{ xMm: number; yMm: number; widthMm: number; heightMm: number; maskAssetId: string }>;

export type RemoteJob = Readonly<{
  id: string;
  status: RemoteJobStatus;
  documentId: string;
  documentRevision: number;
  parentVariantId: string;
  sourceAssetId: string;
  sourceHash: string;
  style: AiGenerationRequest["style"];
  attemptCount: number;
  errorCode: string | null;
  resultAssetId: string | null;
  createdAt: string;
  updatedAt: string;
}>;

export type RemoteSavedGeneration = Readonly<{
  id: string;
  style: AiGenerationRequest["style"];
  inkColor: string;
  mediaType: "image/png" | "image/jpeg";
  widthPx: number;
  heightPx: number;
  byteLength: number;
  contentHash: string;
  createdAt: string;
}>;

export class ProductionAiClientError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(code: string, status: number, message = "The AI service could not complete the request.") {
    super(message);
    this.name = "ProductionAiClientError";
    this.code = code;
    this.status = status;
  }
}

export type ProductionAiJobClientOptions = Readonly<{
  baseUrl?: string;
  fetchImpl?: typeof fetch;
  pollIntervalMs?: number;
}>;

function apiRecord(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new ProductionAiClientError("unreadable_response", 502);
  return value as Record<string, unknown>;
}

async function apiJson(response: Response): Promise<Record<string, unknown>> {
  let value: unknown;
  try { value = await response.json(); }
  catch { throw new ProductionAiClientError("unreadable_response", response.status); }
  const body = apiRecord(value);
  if (!response.ok) {
    const error = body.error;
    const details = error && typeof error === "object" && !Array.isArray(error) ? error as Record<string, unknown> : {};
    throw new ProductionAiClientError(typeof details.code === "string" ? details.code : "request_failed", response.status, typeof details.message === "string" ? details.message : undefined);
  }
  return body;
}

function remoteJob(value: unknown): RemoteJob {
  const job = apiRecord(value);
  if (typeof job.id !== "string" || !["queued", "running", "cancel-requested", "succeeded", "failed", "cancelled"].includes(String(job.status))) {
    throw new ProductionAiClientError("unreadable_response", 502);
  }
  return job as unknown as RemoteJob;
}

function remoteAsset(value: unknown): RemoteAsset {
  const asset = apiRecord(value);
  if (typeof asset.id !== "string" || typeof asset.contentHash !== "string" || (asset.mediaType !== "image/png" && asset.mediaType !== "image/jpeg")) {
    throw new ProductionAiClientError("unreadable_response", 502);
  }
  return asset as unknown as RemoteAsset;
}

function remoteSavedGeneration(value: unknown): RemoteSavedGeneration {
  const generation = apiRecord(value);
  if (typeof generation.id !== "string" || !["outline", "standard", "hatching"].includes(String(generation.style)) ||
      (generation.mediaType !== "image/png" && generation.mediaType !== "image/jpeg") ||
      !Number.isSafeInteger(generation.widthPx) || !Number.isSafeInteger(generation.heightPx) ||
      !Number.isSafeInteger(generation.byteLength) || typeof generation.createdAt !== "string" ||
      typeof generation.inkColor !== "string" || !/^#[0-9a-f]{6}$/i.test(generation.inkColor) ||
      typeof generation.contentHash !== "string" || !/^[a-f0-9]{64}$/.test(generation.contentHash)) {
    throw new ProductionAiClientError("unreadable_response", 502);
  }
  return generation as unknown as RemoteSavedGeneration;
}

function abortableDelay(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) { reject(new DOMException("Cancelled", "AbortError")); return; }
    const timeout = window.setTimeout(() => { signal.removeEventListener("abort", abort); resolve(); }, ms);
    const abort = () => { window.clearTimeout(timeout); reject(new DOMException("Cancelled", "AbortError")); };
    signal.addEventListener("abort", abort, { once: true });
  });
}

function matchesImageSignature(bytes: Uint8Array, mediaType: "image/png" | "image/jpeg"): boolean {
  if (mediaType === "image/png") return bytes.length >= 24 && [137, 80, 78, 71, 13, 10, 26, 10].every((value, index) => bytes[index] === value);
  return bytes.length >= 4 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes.at(-2) === 0xff && bytes.at(-1) === 0xd9;
}

async function sha256(bytes: ArrayBuffer): Promise<string> {
  const digest = await globalThis.crypto.subtle.digest("SHA-256", bytes);
  return [...new Uint8Array(digest)].map((value) => value.toString(16).padStart(2, "0")).join("");
}

async function privateImage(response: Response, invalidCode: string): Promise<Readonly<{ blob: Blob; contentHash: string }>> {
  if (!response.ok) await apiJson(response);
  const contentType = response.headers.get("content-type")?.split(";", 1)[0];
  const contentHash = response.headers.get("x-content-hash");
  if ((contentType !== "image/png" && contentType !== "image/jpeg") || !contentHash || !/^[a-f0-9]{64}$/.test(contentHash)) throw new ProductionAiClientError("unreadable_response", 502);
  const buffer = await response.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  if (!matchesImageSignature(bytes, contentType) || await sha256(buffer) !== contentHash) throw new ProductionAiClientError(invalidCode, 502, "The AI service returned an invalid image.");
  return { blob: new Blob([buffer], { type: contentType }), contentHash };
}

export class ProductionAiJobClient {
  readonly #baseUrl: string;
  readonly #fetch: typeof fetch;
  readonly #pollIntervalMs: number;

  constructor(options: ProductionAiJobClientOptions = {}) {
    this.#baseUrl = (options.baseUrl ?? `${import.meta.env.BASE_URL}api/v1`).replace(/\/$/, "");
    this.#fetch = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
    this.#pollIntervalMs = options.pollIntervalMs ?? 1_000;
  }

  async uploadAsset(bytes: Blob, signal: AbortSignal, purpose: "source" | "selection-mask" = "source"): Promise<RemoteAsset> {
    const response = await this.#fetch(`${this.#baseUrl}/ai-assets`, { method: "POST", credentials: "same-origin", headers: { "Content-Type": bytes.type, "X-AI-Asset-Purpose": purpose }, body: bytes, signal });
    return remoteAsset((await apiJson(response)).asset);
  }

  async createJob(request: AiGenerationRequest & StencilGenerationSettings, ownedSourceAssetId: string, region?: RemoteRegionSelection): Promise<RemoteJob> {
    const response = await this.#fetch(`${this.#baseUrl}/stencil-jobs`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", "Idempotency-Key": request.idempotencyKey },
      body: JSON.stringify({
        sourceAssetId: ownedSourceAssetId,
        sourceHash: request.sourceHash,
        documentId: request.documentId,
        documentRevision: request.revision,
        parentVariantId: request.parentVariantId,
        style: request.style,
        profile: request.profile,
        promptOptions: request.promptOptions,
        privateInstructions: request.privateInstructions,
        ...(region ? { region } : {}),
      }),
      signal: request.signal,
    });
    return remoteJob((await apiJson(response)).job);
  }

  async getJob(jobId: string, signal: AbortSignal): Promise<RemoteJob> {
    const response = await this.#fetch(`${this.#baseUrl}/stencil-jobs/${encodeURIComponent(jobId)}`, { credentials: "same-origin", signal });
    return remoteJob((await apiJson(response)).job);
  }

  async waitForTerminal(job: RemoteJob, signal: AbortSignal): Promise<RemoteJob> {
    let current = job;
    while (["queued", "running", "cancel-requested"].includes(current.status)) {
      await abortableDelay(this.#pollIntervalMs, signal);
      current = await this.getJob(current.id, signal);
    }
    return current;
  }

  async getResult(jobId: string, signal: AbortSignal): Promise<Readonly<{ blob: Blob; contentHash: string }>> {
    const response = await this.#fetch(`${this.#baseUrl}/stencil-jobs/${encodeURIComponent(jobId)}/result`, { credentials: "same-origin", signal });
    return privateImage(response, "invalid_result");
  }

  async getSavedGenerationSource(jobId: string, signal: AbortSignal): Promise<Readonly<{ blob: Blob; contentHash: string }>> {
    const response = await this.#fetch(`${this.#baseUrl}/stencil-library/${encodeURIComponent(jobId)}/source`, { credentials: "same-origin", signal });
    return privateImage(response, "invalid_source");
  }

  async listSavedGenerations(signal: AbortSignal): Promise<RemoteSavedGeneration[]> {
    const response = await this.#fetch(`${this.#baseUrl}/stencil-library`, { credentials: "same-origin", signal });
    const generations = (await apiJson(response)).generations;
    if (!Array.isArray(generations)) throw new ProductionAiClientError("unreadable_response", 502);
    return generations.map(remoteSavedGeneration);
  }

  savedResultUrl(jobId: string): string {
    return `${this.#baseUrl}/stencil-jobs/${encodeURIComponent(jobId)}/result`;
  }

  async deleteSavedGeneration(jobId: string, signal: AbortSignal): Promise<void> {
    const response = await this.#fetch(`${this.#baseUrl}/stencil-library/${encodeURIComponent(jobId)}`, { method: "DELETE", credentials: "same-origin", signal });
    if (!response.ok) await apiJson(response);
  }

  async updateSavedGenerationInk(jobId: string, inkColor: string, signal: AbortSignal): Promise<void> {
    const response = await this.#fetch(`${this.#baseUrl}/stencil-library/${encodeURIComponent(jobId)}`, {
      method: "PATCH", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ inkColor }), signal,
    });
    if (!response.ok) await apiJson(response);
  }

  async cancel(jobId: string, signal: AbortSignal): Promise<"cancel-requested" | "cancelled" | "already-terminal"> {
    const response = await this.#fetch(`${this.#baseUrl}/stencil-jobs/${encodeURIComponent(jobId)}`, { method: "DELETE", credentials: "same-origin", signal });
    const status = (await apiJson(response)).status;
    if (status !== "cancel-requested" && status !== "cancelled" && status !== "already-terminal") throw new ProductionAiClientError("unreadable_response", 502);
    return status;
  }
}

export type OwnedSourceResolver = (request: AiGenerationRequest) => Promise<Readonly<{ assetId: string }>>;
export type CandidateMaterializer = (input: Readonly<{
  request: AiGenerationRequest;
  job: RemoteJob;
  result: Blob;
  resultHash: string;
}>) => Promise<AiCandidate>;

/** Thin adapter: server jobs produce immutable bytes; local composition creates/adopts the candidate. */
export class ProductionAiCandidateAdapter implements AiCandidateAdapter {
  readonly #client: ProductionAiJobClient;
  readonly #resolveSource: OwnedSourceResolver;
  readonly #materialize: CandidateMaterializer;

  constructor(client: ProductionAiJobClient, resolveSource: OwnedSourceResolver, materialize: CandidateMaterializer) {
    this.#client = client;
    this.#resolveSource = resolveSource;
    this.#materialize = materialize;
  }

  async requestCandidate(request: AiGenerationRequest): Promise<AiCandidate> {
    const source = await this.#resolveSource(request);
    let job = await this.#client.createJob(request, source.assetId);
    try {
      job = await this.#client.waitForTerminal(job, request.signal);
    } catch (error) {
      if (request.signal.aborted) {
        const cleanup = new AbortController();
        await this.#client.cancel(job.id, cleanup.signal).catch(() => undefined);
      }
      throw error;
    }
    if (job.status !== "succeeded") throw new ProductionAiClientError(job.errorCode ?? job.status, 502);
    if (job.documentId !== request.documentId || job.documentRevision !== request.revision || job.sourceHash !== request.sourceHash || job.parentVariantId !== request.parentVariantId) {
      throw new ProductionAiClientError("stale_job", 409, "The stencil changed. Review the area again.");
    }
    const result = await this.#client.getResult(job.id, request.signal);
    const candidate = await this.#materialize({ request, job, result: result.blob, resultHash: result.contentHash });
    if (candidate.jobId !== job.id || candidate.documentId !== request.documentId || candidate.revision !== request.revision || candidate.sourceHash !== request.sourceHash) {
      throw new ProductionAiClientError("stale_candidate", 409, "The stencil changed. Review the area again.");
    }
    return candidate;
  }

  cancel(jobId: string, _cancellationId: string, signal: AbortSignal): Promise<"cancel-requested" | "cancelled" | "already-terminal"> {
    return this.#client.cancel(jobId, signal);
  }
}
